export type RecoveryIdentityReference = { issuer?: string; subject?: string };

export type RecoveryDiagnosticState = {
  status?: string;
  issues: string[];
};

export type RecoveryEvidenceReceipt = {
  evidenceId?: string;
  evidenceType?: string;
  issuerRef?: string;
  recordedAt?: number;
  status?: string;
  supersedesEvidenceId?: string;
  consumedByOperationId?: string;
};

export type RecoveryIdentityContinuitySnapshot = {
  applicationUserId?: string;
  authIdentity?: RecoveryIdentityReference;
  profileStatus?: string;
  primaryPhone?: string;
};

export type RecoveryExecutionAttempt = {
  attemptNumber?: number;
  operationId?: string;
  attemptedAt?: number;
  completedAt?: number;
  authOutcomeRecordedAt?: number;
  outcome?: string;
  failureCode?: string;
  authMutationOccurred?: boolean;
};

export type RecoveryAuditEntry = {
  eventId: string;
  caseVersion?: number;
  operationId?: string;
  action: string;
  fromStatus?: string;
  toStatus?: string;
  actor: {
    type?: string;
    applicationUserId?: string;
    displayName?: string;
  };
  timestamp: number;
  reason?: string;
  outcome?: string;
  step?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  technicalIds?: Record<string, string | number | boolean>;
};

export type RecoveryReservationRelease = {
  status?: 'PENDING' | 'RELEASED';
  reason?: 'CLAIMANT_CANCELLED' | 'ADMIN_CANCELLED' | 'ADMIN_REJECTED' | 'PROFILE_EMAIL_CHANGED';
  targetTerminalStatus?: 'CANCELLED' | 'REJECTED';
  requestedAt?: number;
  completedAt?: number;
  failureCode?: string;
};

export type RecoveryFullEvidence = {
  applicationUserId?: string;
  recoveryNoticeAcceptedAt?: number;
  claimantContacts?: {
    oldPrimaryPhone?: string;
    recoveryEmail?: string;
    newPrimaryPhone?: string;
  };
  claimantContactEvidence?: { status: 'AVAILABLE' | 'UNAVAILABLE'; reason?: string };
  diagnostics?: RecoveryDiagnosticState;
  integrity?: RecoveryDiagnosticState;
  evidenceSnapshot: {
    locator?: { type?: string; version?: number; previousPrimaryPhone?: string };
    verifiedProfileEmail?: string;
    verifiedProfileEmailVerifiedAt?: number;
    proposedNewPrimaryPhone?: string;
    contextStatements: Array<{ promptKey: string; answer: string }>;
    receipts: RecoveryEvidenceReceipt[];
  };
  approvedManifest?: {
    manifestId?: string;
    manifestSchemaVersion?: number;
    caseVersion?: number;
    targetApplicationUserId?: string;
    allowedAction?: string;
    authoritySnapshotRef?: string;
    frozenAt?: number;
    evidenceReferences: Array<{ evidenceId?: string; evidenceType?: string }>;
  };
  identityContinuity: {
    before?: RecoveryIdentityContinuitySnapshot;
    after?: RecoveryIdentityContinuitySnapshot;
  };
  displacedIdentity?: RecoveryIdentityReference;
  replacementClearance?: { reclaimable?: boolean; checks: Record<string, boolean> };
  relationships: {
    propertyMembershipCount?: number;
    organizationMembershipCount?: number;
    unchangedSinceApproval?: boolean | null;
    authorityWorkflowConflicts: string[];
  };
  execution: {
    operationId?: string;
    steps: Record<string, number>;
    attempts: RecoveryExecutionAttempt[];
    reconciliation?: {
      status?: string;
      reason?: string;
      recordedAt?: number;
      resolvedAt?: number;
    };
    claimantSessionCleanup?: { recordedAt?: number; outcome?: string };
    deviceCleanup?: { completedAt?: number; deletedDeviceTokens?: number };
    finalOutcome?: { outcome?: string; recordedAt?: number; reason?: string };
  };
  /** Safe restricted-detail lifecycle summary; request bindings and operation IDs remain server-only. */
  reservationRelease?: RecoveryReservationRelease;
  audit: RecoveryAuditEntry[];
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asDiagnosticState(value: unknown): RecoveryDiagnosticState | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    status: asString(source.status),
    issues: Array.isArray(source.issues)
      ? source.issues.filter((issue): issue is string => typeof issue === 'string' && Boolean(issue.trim()))
      : [],
  };
}

function asIdentity(value: unknown): RecoveryIdentityReference | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return { issuer: asString(source.issuer), subject: asString(source.subject) };
}

function asReceipt(value: unknown): RecoveryEvidenceReceipt | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    evidenceId: asString(source.evidenceId),
    evidenceType: asString(source.evidenceType),
    issuerRef: asString(source.issuerRef),
    recordedAt: asNumber(source.recordedAt),
    status: asString(source.status),
    supersedesEvidenceId: asString(source.supersedesEvidenceId),
    consumedByOperationId: asString(source.consumedByOperationId),
  };
}

function asContinuitySnapshot(value: unknown): RecoveryIdentityContinuitySnapshot | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    applicationUserId: asString(source.applicationUserId),
    authIdentity: asIdentity(source.authIdentity),
    profileStatus: asString(source.profileStatus),
    primaryPhone: asString(source.primaryPhone),
  };
}

function asExecutionAttempt(value: unknown): RecoveryExecutionAttempt | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    attemptNumber: asNumber(source.attemptNumber),
    operationId: asString(source.operationId),
    attemptedAt: asNumber(source.attemptedAt),
    completedAt: asNumber(source.completedAt),
    authOutcomeRecordedAt: asNumber(source.authOutcomeRecordedAt),
    outcome: asString(source.outcome),
    failureCode: asString(source.failureCode),
    authMutationOccurred: asBoolean(source.authMutationOccurred),
  };
}

function asPrimitiveRecord(value: unknown): Record<string, string | number | boolean> | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return Object.fromEntries(
    Object.entries(source).flatMap(([key, entry]): Array<[string, string | number | boolean]> => {
      if (isForbiddenSecretKey(key)) return [];
      return typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean' ? [[key, entry]] : [];
    }),
  );
}

function isForbiddenSecretKey(key: string): boolean {
  const normalized = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return [
    'otp',
    'rawotp',
    'otphash',
    'otpsalt',
    'verificationcode',
    'challengecode',
    'challengeid',
    'codehash',
    'codesalt',
    'recoverysession',
    'sessionsecret',
    'accesstoken',
    'refreshtoken',
    'idtoken',
    'bearertoken',
    'providersecret',
    'privatekey',
    'digest',
    'fingerprint',
  ].some((secretName) => normalized === secretName || normalized.endsWith(secretName));
}

function sanitizeAuditValue(value: unknown): unknown {
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) return value.map(sanitizeAuditValue);
  const source = asRecord(value);
  if (!source) return undefined;
  return Object.fromEntries(
    Object.entries(source).flatMap(([key, entry]) => {
      if (isForbiddenSecretKey(key)) return [];
      const sanitized = sanitizeAuditValue(entry);
      return sanitized === undefined ? [] : [[key, sanitized]];
    }),
  );
}

function asAuditRecord(value: unknown): Record<string, unknown> | undefined {
  return asRecord(sanitizeAuditValue(value));
}

function asContextStatements(value: unknown): Array<{ promptKey: string; answer: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const answer = asRecord(entry);
    const promptKey = asString(answer?.promptKey);
    const text = asString(answer?.answer);
    return promptKey && text ? [{ promptKey, answer: text }] : [];
  });
}

export function parseRecoveryFullEvidence(value: unknown): RecoveryFullEvidence | null {
  const source = asRecord(value);
  if (!source) return null;

  const claimantContacts = asRecord(source.claimantContacts);
  const claimantContactEvidence = asRecord(source.claimantContactEvidence);
  const evidenceSnapshot = asRecord(source.evidenceSnapshot);
  const locator = asRecord(evidenceSnapshot?.locator);
  const receipts = asRecord(evidenceSnapshot?.receipts);
  const approvedManifest = asRecord(source.approvedManifest);
  const identityContinuity = asRecord(source.identityContinuity);
  const relationships = asRecord(source.relationships);
  const clearance = asRecord(source.displacedIdentityClearance);
  const checks = asRecord(clearance?.checks);
  const execution = asRecord(source.execution);
  const executionSteps = asRecord(execution?.steps);
  const reconciliation = asRecord(execution?.reconciliation);
  const claimantSessionCleanup = asRecord(execution?.claimantSessionCleanup);
  const deviceCleanup = asRecord(execution?.deviceCleanup);
  const finalOutcome = asRecord(execution?.finalOutcome);
  const reservationRelease = asRecord(source.reservationRelease);

  const parsedReceipts = [receipts?.email, receipts?.context, receipts?.phone].flatMap((receipt) => {
    const parsed = asReceipt(receipt);
    return parsed ? [parsed] : [];
  });

  return {
    applicationUserId: asString(source.applicationUserId),
    recoveryNoticeAcceptedAt: asNumber(source.recoveryNoticeAcceptedAt),
    claimantContacts: claimantContacts
      ? {
          oldPrimaryPhone: asString(claimantContacts.oldPrimaryPhone),
          recoveryEmail: asString(claimantContacts.recoveryEmail),
          newPrimaryPhone: asString(claimantContacts.newPrimaryPhone),
        }
      : undefined,
    claimantContactEvidence:
      claimantContactEvidence?.status === 'AVAILABLE' || claimantContactEvidence?.status === 'UNAVAILABLE'
        ? {
            status: claimantContactEvidence.status,
            reason: asString(claimantContactEvidence.reason),
          }
        : undefined,
    diagnostics: asDiagnosticState(source.diagnostics),
    integrity: asDiagnosticState(source.integrity),
    evidenceSnapshot: {
      locator: locator
        ? {
            type: asString(locator.type),
            version: asNumber(locator.version),
            previousPrimaryPhone: asString(locator.previousPrimaryPhone),
          }
        : undefined,
      verifiedProfileEmail: asString(evidenceSnapshot?.verifiedProfileEmail),
      verifiedProfileEmailVerifiedAt: asNumber(evidenceSnapshot?.verifiedProfileEmailVerifiedAt),
      proposedNewPrimaryPhone: asString(evidenceSnapshot?.proposedNewPrimaryPhone),
      contextStatements: asContextStatements(evidenceSnapshot?.contextStatements),
      receipts: parsedReceipts,
    },
    approvedManifest: approvedManifest
      ? {
          manifestId: asString(approvedManifest.manifestId),
          manifestSchemaVersion: asNumber(approvedManifest.manifestSchemaVersion),
          caseVersion: asNumber(approvedManifest.caseVersion),
          targetApplicationUserId: asString(approvedManifest.targetApplicationUserId),
          allowedAction: asString(approvedManifest.allowedAction),
          authoritySnapshotRef: asString(approvedManifest.authoritySnapshotRef),
          frozenAt: asNumber(approvedManifest.frozenAt),
          evidenceReferences: Array.isArray(approvedManifest.evidenceReferences)
            ? approvedManifest.evidenceReferences.flatMap((entry) => {
                const reference = asRecord(entry);
                return reference
                  ? [
                      {
                        evidenceId: asString(reference.evidenceId),
                        evidenceType: asString(reference.evidenceType),
                      },
                    ]
                  : [];
              })
            : [],
        }
      : undefined,
    identityContinuity: {
      before: asContinuitySnapshot(identityContinuity?.before),
      after: asContinuitySnapshot(identityContinuity?.after),
    },
    displacedIdentity: asIdentity(source.displacedIdentity),
    replacementClearance: clearance
      ? {
          reclaimable: asBoolean(clearance.reclaimable),
          checks: Object.fromEntries(
            Object.entries(checks ?? {}).flatMap(
              ([key, entry]): Array<[string, boolean]> => (typeof entry === 'boolean' ? [[key, entry]] : []),
            ),
          ),
        }
      : undefined,
    relationships: {
      propertyMembershipCount: asNumber(relationships?.propertyMembershipCount),
      organizationMembershipCount: asNumber(relationships?.organizationMembershipCount),
      unchangedSinceApproval:
        typeof relationships?.unchangedSinceApproval === 'boolean' ? relationships.unchangedSinceApproval : null,
      authorityWorkflowConflicts: Array.isArray(relationships?.authorityWorkflowConflicts)
        ? relationships.authorityWorkflowConflicts.filter((entry): entry is string => typeof entry === 'string')
        : [],
    },
    execution: {
      operationId: asString(execution?.operationId),
      steps: Object.fromEntries(
        Object.entries(executionSteps ?? {}).flatMap(([key, entry]): Array<[string, number]> => {
          const timestamp = asNumber(entry);
          return timestamp === undefined ? [] : [[key, timestamp]];
        }),
      ),
      attempts: Array.isArray(execution?.attempts)
        ? execution.attempts.flatMap((entry) => {
            const attempt = asExecutionAttempt(entry);
            return attempt ? [attempt] : [];
          })
        : [],
      reconciliation: reconciliation
        ? {
            status: asString(reconciliation.status),
            reason: asString(reconciliation.reason),
            recordedAt: asNumber(reconciliation.recordedAt),
            resolvedAt: asNumber(reconciliation.resolvedAt),
          }
        : undefined,
      claimantSessionCleanup: claimantSessionCleanup
        ? {
            recordedAt: asNumber(claimantSessionCleanup.recordedAt),
            outcome: asString(claimantSessionCleanup.outcome),
          }
        : undefined,
      deviceCleanup: deviceCleanup
        ? {
            completedAt: asNumber(deviceCleanup.completedAt),
            deletedDeviceTokens: asNumber(deviceCleanup.deletedDeviceTokens),
          }
        : undefined,
      finalOutcome: finalOutcome
        ? {
            outcome: asString(finalOutcome.outcome),
            recordedAt: asNumber(finalOutcome.recordedAt),
            reason: asString(finalOutcome.reason),
          }
        : undefined,
    },
    reservationRelease: reservationRelease
      ? {
          status:
            reservationRelease.status === 'PENDING' || reservationRelease.status === 'RELEASED'
              ? reservationRelease.status
              : undefined,
          reason:
            reservationRelease.reason === 'CLAIMANT_CANCELLED' ||
            reservationRelease.reason === 'ADMIN_CANCELLED' ||
            reservationRelease.reason === 'ADMIN_REJECTED' ||
            reservationRelease.reason === 'PROFILE_EMAIL_CHANGED'
              ? reservationRelease.reason
              : undefined,
          targetTerminalStatus:
            reservationRelease.targetTerminalStatus === 'CANCELLED' ||
            reservationRelease.targetTerminalStatus === 'REJECTED'
              ? reservationRelease.targetTerminalStatus
              : undefined,
          requestedAt: asNumber(reservationRelease.requestedAt),
          completedAt: asNumber(reservationRelease.completedAt),
          failureCode: asString(reservationRelease.failureCode),
        }
      : undefined,
    audit: Array.isArray(source.audit)
      ? source.audit.flatMap((entry, index) => {
          const audit = asRecord(entry);
          const action = asString(audit?.action);
          const timestamp = asNumber(audit?.timestamp);
          if (!audit || !action || timestamp === undefined) return [];
          const actor = asRecord(audit.actor);
          return [
            {
              eventId: asString(audit.eventId) ?? `recovery-audit-${index}`,
              caseVersion: asNumber(audit.caseVersion),
              operationId: asString(audit.operationId),
              action,
              fromStatus: asString(audit.fromStatus),
              toStatus: asString(audit.toStatus),
              actor: {
                type: asString(actor?.type),
                applicationUserId: asString(actor?.applicationUserId),
                displayName: asString(actor?.displayName),
              },
              timestamp,
              reason: asString(audit.reason),
              outcome: asString(audit.outcome),
              step: asString(audit.step),
              before: asAuditRecord(audit.before),
              after: asAuditRecord(audit.after),
              technicalIds: asPrimitiveRecord(audit.technicalIds),
            },
          ];
        })
      : [],
  };
}
